"use client";

import { useState, useEffect, useRef, use } from "react";
import { useParams } from "next/navigation";
import HeaderInner from "@/components/HeaderInner";
import Image from "next/image";
import * as ReactDOM from "react-dom";
import FilterBar from "@/app/cin_booking_engine/Filterbar";
import { BookingEngineProvider } from "@/app/cin_context/BookingEngineContext";
import TigerLoader from "@/components/TigerLoader";

export default function EventPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [propertyId, setPropertyId] = useState(null);
  const [propertyData, setPropertyData] = useState([]);
  const { propertySlug } = useParams();
  const [slides, setSlides] = useState([]);
  const [expandedIndexes, setExpandedIndexes] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const filterBarRef = useRef(null);
  const [staahPropertyId, setStaahPropertyId] = useState(null);
  const [cityDetails, setCityDetails] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [brandId, setBrandId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [brandData, setBrandData] = useState([]);
  const [venueBannerData, setVenueBannerData] = useState([]);
  const [capacityModalOpen, setCapacityModalOpen] = useState(false);
  const [selectedCapacityVenue, setSelectedCapacityVenue] = useState(null);

  const capacityLabels = [
    { key: "theater", label: "Theater" },
    { key: "roundTable", label: "Round Table" },
    { key: "cluster", label: "Cluster" },
    { key: "boardRoom", label: "Board Room" },
    { key: "classRoom", label: "Class Room" },
    { key: "ushape", label: "U-Shape" },
    { key: "receptionLobby", label: "Reception / Lobby" },
    { key: "floating", label: "Floating" },
    { key: "social", label: "Social" },
    { key: "wedding", label: "Wedding" },
    { key: "fishboneSetting", label: "Fishbone Setting" },
  ];

  const handleCapacityChartClick = (event) => {
    setSelectedCapacityVenue(event);
    setCapacityModalOpen(true);
  };

  const brandColors = {
    1: "#bf1e2d",
    4: "#bf1e2d",
    2: "#263997",
    3: "#f04e02",
    5: "#febb19",
    6: "#f9e12e",
  };

  const backgroundColor = brandColors[brandId] || "#f26624";

  /* ─────────────────────────────────────────────
     EVENT FORM STATES + SUBMIT LOGIC  (NEW)
  ─────────────────────────────────────────────── */
  const [eventForm, setEventForm] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });

  const [eventNotice, setEventNotice] = useState("");
  const [eventLoading, setEventLoading] = useState(false);

  const handleEventFormChange = (e) => {
    const { name, value } = e.target;
    setEventForm((p) => ({ ...p, [name]: value }));
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setEventLoading(true);
    setEventNotice("");

    const payload = {
      cityId: String(propertyData?.cityId || ""),
      propertyId: String(propertyData?.propertyId || ""),
      firstName: String(eventForm.name || ""),
      lastName: "",
      mobileNo: String(eventForm.mobile || ""),
      email: String(eventForm.email || ""),
      eventDate: "",
      eventType: "Event Enquiry",
      roomsRequired: false,
      noOfRooms: "0",
      noOfGuests: "0",
      checkIn: "",
      checkOut: "",
      requestFrom: "Event Enquiry",
      remarks: String(eventForm.message || ""),
      remarks1: propertyData?.propertyName || "",
      remarks2: "",
    };

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/common/EnquireNow`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      let data = null;
      try {
        data = await res.clone().json();
      } catch {}

      if (!res.ok) {
        setEventNotice("Failed to submit. Please try again.");
        setEventLoading(false);
        return;
      }

      if (data?.errorCode && String(data.errorCode) !== "0") {
        setEventNotice(data.errorMessage || "Something went wrong.");
        setEventLoading(false);
        return;
      }

      setEventNotice("Your request has been submitted successfully.");
      setEventForm({ name: "", email: "", mobile: "", message: "" });

      setTimeout(() => setIsModalOpen(false), 1500);
    } catch {
      setEventNotice("Submission failed. Try again later.");
    } finally {
      setEventLoading(false);
    }
  };

  /* ─────────────────────────────────────────────── */

  useEffect(() => {
    window.scrollTo(0, 0);
    const storedBrandId = localStorage.getItem("brandId");
    setBrandId(storedBrandId);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Brand data
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/property/GetBrandList`,
        );
        const json = await res.json();
        setBrandData(json.data || []);
      } catch (e) {
        console.error("Error fetching brands:", e);
      }
    })();
  }, []);

  // Property info
  useEffect(() => {
    if (!propertySlug) return;

    const fetchPropertyIdFromSlug = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/property/GetPropertyList`,
        );
        const json = await res?.json();
        if (json.errorMessage !== "success") return null;

        const found = json.data.find((p) => p.propertySlug === propertySlug);
        setPropertyData(found);
        setCityDetails({
          label: found?.cityName,
          value: found?.cityId,
          property_Id: found?.staahPropertyId,
        });
        setStaahPropertyId(found?.staahPropertyId);
        return found?.propertyId || null;
      } catch (error) {
        console.error("Error fetching property list:", error);
        return null;
      }
    };

    const loadPropertyId = async () => {
      const id = await fetchPropertyIdFromSlug(propertySlug);
      setPropertyId(id);
    };

    loadPropertyId();
  }, [propertySlug]);

  useEffect(() => {
    const fetchEventsDetails = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/property/GetPropertyVenueBanner?propertyId=${propertyId}`,
        );
        const json = await res.json();
        if (json?.errorCode === "0" && Array.isArray(json.data)) {
          setVenueBannerData(json.data?.[0]);
          const venueItems = [];
          json.data.forEach((entry) => {
            (entry.roomsInfo || []).forEach((venue) => {
              if (
                venue.venuesImages?.[0]?.images ||
                venue.venueName ||
                venue.venueDesc
              ) {
                venueItems.push({
                  bg: venue.venuesImages?.[0]?.images || "",
                  title: venue.venueName || "",
                  description: venue.venueDesc || "",
                  capacity: {
                    theater: venue.theater,
                    roundTable: venue.roundTable,
                    cluster: venue.cluster,
                    boardRoom: venue.boardRoom,
                    classRoom: venue.classRoom,
                    ushape: venue.ushape,
                    receptionLobby: venue.receptionLobby,
                    floating: venue.floating,
                    social: venue.social,
                    wedding: venue.wedding,
                    fishboneSetting: venue.fishboneSetting,
                  },
                });
              }
            });
          });
          setSlides(venueItems);
        }
      } catch (err) {
        console.error("Error fetching event details:", err);
      } finally {
        setIsPageLoading(false);
      }
    };

    if (propertyId) fetchEventsDetails();
  }, [propertyId]);

  const toggleDescription = (index) =>
    setExpandedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );

  const handlePropertyBookNow = () => {
    setOpen(!isOpen);
    setShowFilterBar(!showFilterBar);
    if (filterBarRef.current) {
      filterBarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      const firstInput = filterBarRef.current.querySelector(
        "input, select, button",
      );
      if (firstInput) firstInput.focus();
    }
  };

  if (isPageLoading) return <TigerLoader />;

  return (
    <main className="bg-white" style={{ overflowY: "auto" }}>
      <HeaderInner
        brandData={brandData}
        brandId={brandId}
        propertyData={propertyData}
        backgroundColor={backgroundColor}
        propertyId={propertyData?.propertyId}
        onClick={handlePropertyBookNow}
      />

      <section ref={filterBarRef} className="position-relative">
        <section
          className="position-fixed start-0 w-100 bg-white shadow"
          ref={filterBarRef}
        >
          {showFilterBar &&
            typeof window !== "undefined" &&
            ReactDOM.createPortal(
              <section className="filter-bar-hotels-cin">
                <BookingEngineProvider>
                  <FilterBar
                    selectedProperty={parseInt(staahPropertyId)}
                    cityDetails={cityDetails}
                    openBookingBar={showFilterBar}
                    onClose={(isReload) => {
                      setOpen(!isOpen);
                      setShowFilterBar(!showFilterBar);
                      if (isReload) {
                        window.location.reload();
                      }
                    }}
                    isCloseButtonShow="true"
                    backgroundColor={backgroundColor}
                  />
                </BookingEngineProvider>
              </section>,
              document.body,
            )}
        </section>
      </section>

      {/* CONTENT STARTS */}
      <div style={{ paddingTop: "130px" }}></div>

      <div className="text-center mb-16 max-w-3xl mx-auto">
        <h1
          className="uppercase custom-hotel-header fw-semibold md:text-[23px] mb-3 px-3"
          style={{ color: backgroundColor }}
        >
          {venueBannerData?.venueBannerTitle}
        </h1>

        <div
          className="w-10 h-1 mx-auto mt-2 mb-4"
          style={{ background: backgroundColor }}
        ></div>
        <p className="mt-4 text-gray-600 custom-hotel-description px-3">
          <span
            dangerouslySetInnerHTML={{
              __html:
                isExpanded ||
                (venueBannerData?.venueBannerDesc?.replace(/<[^>]*>/g, "")
                  .length || 0) <= (isMobile ? 100 : 150)
                  ? venueBannerData?.venueBannerDesc || ""
                  : `${venueBannerData?.venueBannerDesc
                      ?.replace(/<[^>]*>/g, "")
                      .slice(0, isMobile ? 100 : 150)}...`,
            }}
          />

          {venueBannerData?.venueBannerDesc?.replace(/<[^>]*>/g, "").length >
            (isMobile ? 100 : 150) && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="underline fw-semibold ml-2"
              style={{ color: backgroundColor }}
            >
              {isExpanded ? "Read Less" : "Read More"}
            </button>
          )}
        </p>
      </div>

      {/* Event Sections */}
      <div className="space-y-24 mb-5 container">
        {slides?.map((event, idx) => (
          <div
            key={idx}
            className={`relative flex flex-col md:flex-row ${
              idx % 2 !== 0 ? "md:flex-row-reverse" : ""
            } md:items-center md:gap-12`}
          >
            <div className="md:w-1/2">
              <Image
                src={event?.bg || "/no_image.jpg"}
                alt={event?.title || "Event Image"}
                className="w-full h-[200px] md:h-[350px]"
                height={400}
                width={400}
              />
            </div>

            <div className="md:w-1/2 relative z-10 md:-ml-20 md:-mr-20">
              <div className="bg-white shadow-lg p-8">
                <h3
                  className="custom-hotel-dineHeader font-bold mb-4"
                  style={{ color: backgroundColor }}
                >
                  {event?.title}
                </h3>

                <p className="text-gray-600 mb-6 !text-[14px]">
                  {expandedIndexes.includes(idx) ||
                  event.description.length <= (isMobile ? 100 : 250)
                    ? event.description
                    : `${event.description.slice(0, isMobile ? 100 : 250)}...`}

                  {event.description.length > (isMobile ? 100 : 150) && (
                    <button
                      onClick={() => toggleDescription(idx)}
                      className="underline ml-2 !text-[14px] fw-semibold"
                      style={{ color: backgroundColor }}
                    >
                      {expandedIndexes.includes(idx)
                        ? "Read Less"
                        : "Read More"}
                    </button>
                  )}
                </p>

                <div className="flex gap-4">
                  <button
                    className="px-3 py-2 !text-[10px] md:!text-[12px]"
                    style={{ border: `1px solid ${backgroundColor}` }}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Request a Proposal
                  </button>

  <button
    className="!text-[10px] md:!text-[12px] text-white px-3 py-2 rounded-0 shadow"
    style={{ background: backgroundColor }}
    onClick={() => handleCapacityChartClick(event)}
  >
    Capacity Chart
  </button>

                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MODAL WITH SUBMIT FUNCTION (FINAL) */}
      {isModalOpen && (
        <div className="fixed top-20 inset-0 bg-black/50 flex items-center justify-center z-[10050]">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-lg relative">
            <button
              className="absolute top-2 w-8 h-6 right-2 text-white"
              style={{ background: backgroundColor }}
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>

            <h4
              className="!text-[18px] font-bold mb-4"
              style={{ color: backgroundColor }}
            >
              Request a Proposal
            </h4>

            <p className="text-gray-600 mb-4">
              Fill out the form below and our team will contact you shortly.
            </p>

            <form className="space-y-4" onSubmit={handleEventSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                className="w-full border px-3 mb-2 py-2 rounded-md"
                value={eventForm.name}
                onChange={handleEventFormChange}
                required
              />
              <input
                type="tel"
                name="mobile"
                placeholder="Your Mobile Number"
                className="w-full border px-3 mb-2 py-2 rounded-md"
                value={eventForm.mobile}
                onChange={handleEventFormChange}
                pattern="[0-9]{10}"
                maxLength={10}
                required
              />

              <input
                type="email"
                name="email"
                placeholder="Your Email"
                className="w-full border px-3 mb-2 py-2 rounded-md"
                value={eventForm.email}
                onChange={handleEventFormChange}
                required
              />

              <textarea
                name="message"
                placeholder="Your Message"
                className="w-full border px-3 py-2 mb-4 rounded-md"
                rows="4"
                value={eventForm.message}
                maxLength={300}
                onChange={handleEventFormChange}
                required
              ></textarea>

              {eventNotice && (
                <p className="text-center text-green-600 text-sm">
                  {eventNotice}
                </p>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  className="!text-[12px] text-white px-4 py-2 mb-4 rounded-md"
                  style={{ background: backgroundColor }}
                  disabled={eventLoading}
                >
                  {eventLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CAPACITY CHART MODAL */}
      {capacityModalOpen && selectedCapacityVenue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10050] p-3">
          <div className="bg-white w-full max-w-md rounded-md shadow-lg relative max-h-[85vh] overflow-y-auto">
            <button
              className="absolute top-2.5 right-2.5 w-8 h-7 flex items-center justify-center text-white text-lg z-10"
              style={{ background: backgroundColor }}
              onClick={() => setCapacityModalOpen(false)}
            >
              ✕
            </button>

            <div
              className="px-3 pr-14 py-3"
              style={{ borderBottom: "1px solid #dee2e6" }}
            >
              <h4
                className="!text-[18px] font-bold mb-0"
                style={{ color: backgroundColor }}
              >
                {selectedCapacityVenue?.title}
              </h4>
            </div>

            <div className="px-3 py-4">
              {/* {selectedCapacityVenue?.description && (
                <p className="text-gray-600 mb-4 !text-[14px]">
                  {selectedCapacityVenue.description}
                </p>
              )} */}

              <table className="w-full border-collapse !text-[14px]">
                <thead>
                  <tr style={{ background: backgroundColor }}>
                    <th
                      className="text-left text-white font-semibold px-3 py-2"
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      Type
                    </th>
                    <th
                      className="text-left text-white font-semibold px-3 py-2"
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      Capacity
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {capacityLabels
                    .filter(({ key }) => {
                      const value = selectedCapacityVenue?.capacity?.[key];
                      return value && value !== "N/A" && value !== "-";
                    })
                    .map(({ key, label }) => (
                      <tr
                        key={key}
                        style={{ borderBottom: "1px solid #e5e7eb" }}
                      >
                        <td className="px-3 py-2 font-semibold text-gray-800">
                          {label}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {selectedCapacityVenue.capacity[key]}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
